import { useParams, Link, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { blogPostsEs, getPostsForRegionEs } from "@/lib/blog-data-es";
import { ALL_LOCATIONS } from "@/lib/locations";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ShareBarEs } from "@/components/ShareBarEs";
import { Calendar, ChevronLeft, Facebook, Twitter, Link as LinkIcon, ArrowRight } from "lucide-react";
import { PricingCalculator } from "@/components/PricingCalculator";
import { BlogLeadCapture } from "@/components/BlogLeadCapture";
import { CostOfSuppliesInfographic } from "@/components/CostOfSuppliesInfographic";
import { MaidServiceChecklistDownload } from "@/components/MaidServiceChecklistDownload";
import { MaidVsHouseCleaningInfographic } from "@/components/MaidVsHouseCleaningInfographic";
import { CleaningHabitTrackerDownload } from "@/components/CleaningHabitTrackerDownload";
import { StandardVsDeepSlider } from "@/components/StandardVsDeepSlider";
import { CleaningFrequencyPlanner } from "@/components/CleaningFrequencyPlanner";
import { BlogFAQ } from "@/components/BlogFAQ";
import { useState, useEffect } from "react";

const SpanishBlogPostPage = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  let post = blogPostsEs.find((p) => p.slug === slug);

  if (!post) {
    for (const loc of ALL_LOCATIONS) {
      const localPosts = getPostsForRegionEs(loc.name, loc.regionSlug);
      const found = localPosts.find(p => p.slug === slug);
      if (found) {
        post = found;
        break;
      }
    }
  }

  if (!post) {
    return <Navigate to="/404" />;
  }

  const otherPosts = blogPostsEs.filter((p) => p.slug !== slug).slice(0, 2);

  const headings: { id: string; title: string }[] = [];
  const processedContent = post.content.replace(/<h2>(.*?)<\/h2>/g, (match, title) => {
    const cleanTitle = title.replace(/<[^>]+>/g, '').trim();
    const id = cleanTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ id, title: cleanTitle });
    return `<h2 id="${id}" class="scroll-m-24">${title}</h2>`;
  });

  const parseSpanishDate = (dateStr: string) => {
    try {
      const months: Record<string, string> = {
        enero: "01", febrero: "02", marzo: "03", abril: "04", mayo: "05", junio: "06",
        julio: "07", agosto: "08", septiembre: "09", octubre: "10", noviembre: "11", diciembre: "12"
      };
      const cleanStr = dateStr.toLowerCase().replace(/,/g, '');
      const parts = cleanStr.split(' ');
      let day = "", month = "", year = "";
      for (const part of parts) {
        if (!isNaN(parseInt(part)) && part.length <= 2) day = part.padStart(2, '0');
        else if (!isNaN(parseInt(part)) && part.length === 4) year = part;
        else if (months[part]) month = months[part];
      }
      if (day && month && year) {
        const d = new Date(`${year}-${month}-${day}T00:00:00.000Z`);
        if (!isNaN(d.getTime())) return d.toISOString();
      }
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) return d.toISOString();
      return new Date().toISOString();
    } catch (e) {
      return new Date().toISOString();
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <SEO 
        title={`${post.title} — Uplevel Cleaning`}
        description={post.excerpt}
        keywords={`${post.category.toLowerCase()}, consejos de limpieza, uplevel cleaning`}
        lang="es"
        articleData={{
          title: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: parseSpanishDate(post.date),
          author: post.author
        }}
      />
      <Navbar />
      <main className="flex-1 relative">
        {showStickyCTA && (
          <div className="fixed top-0 left-0 right-0 z-40 bg-primary text-primary-foreground py-3 px-4 shadow-md animate-in slide-in-from-top-4 fade-in duration-300 md:hidden">
            <div className="container flex items-center justify-between">
              <span className="text-sm font-bold truncate mr-4">¿Listo para un hogar impecable?</span>
              <Button asChild size="sm" variant="secondary" className="whitespace-nowrap shrink-0">
                <a href="/book?view=custom-plan">Reservar</a>
              </Button>
            </div>
          </div>
        )}
        {showStickyCTA && (
          <div className="fixed top-20 left-0 right-0 z-40 bg-primary text-primary-foreground py-3 px-4 shadow-md animate-in slide-in-from-top-4 fade-in duration-300 hidden md:block">
            <div className="container flex items-center justify-between max-w-4xl">
              <span className="text-base font-bold">¿Listo para un hogar impecable? Obtén tu precio exacto en 60 segundos.</span>
              <Button asChild size="sm" variant="secondary" className="whitespace-nowrap">
                <a href="/book?view=custom-plan">Ver Precio y Reservar</a>
              </Button>
            </div>
          </div>
        )}
        <article className="py-20">
          <div className="container max-w-4xl">
            <Button asChild variant="ghost" className="mb-8 -ml-4 text-muted-foreground hover:text-primary">
              <Link to="/es/blog">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Volver al Blog
              </Link>
            </Button>

            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <span className="bg-primary/10 text-primary text-[10px] uppercase font-black tracking-widest px-3 py-1 rounded-full">
                  {post.category}
                </span>
                <span className="text-muted-foreground text-sm">• {post.readTime}</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-y py-6">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xs">
                    UC
                  </div>
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{post.date}</span>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <span className="font-semibold text-foreground uppercase tracking-wider text-[10px]">Compartir:</span>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                      className="p-2 rounded-full hover:bg-muted transition-colors"
                      title="Compartir en Facebook"
                    >
                      <Facebook className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`, '_blank')}
                      className="p-2 rounded-full hover:bg-muted transition-colors"
                      title="Compartir en Twitter"
                    >
                      <Twitter className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        toast({
                          title: "¡Enlace copiado!",
                          description: "El enlace del artículo ha sido copiado a su portapapeles.",
                        });
                      }}
                      className="p-2 rounded-full hover:bg-muted transition-colors"
                      title="Copiar Enlace"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="aspect-video rounded-3xl overflow-hidden mb-12 shadow-2xl">
              <img 
                src={post.image} 
                alt={post.title} 
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>

            {headings.length > 0 && (
              <div className="bg-muted/30 p-6 rounded-2xl mb-12 border border-border">
                <h3 className="font-bold text-lg mb-4">Tabla de Contenidos</h3>
                <ul className="space-y-3">
                  {headings.map(h => (
                    <li key={h.id}>
                      <a href={`#${h.id}`} className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                        {h.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div 
              className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />

            {(post.slug.includes("house-cleaning-cost") || post.slug.includes("pricing") || post.slug.includes("worth-it")) && (
              <CostOfSuppliesInfographic />
            )}

            {post.slug.includes("deep-cleaning-vs-standard-cleaning") && (
              <StandardVsDeepSlider />
            )}

            {post.slug.includes("maid-vs-house-cleaner") && (
              <MaidVsHouseCleaningInfographic />
            )}

            {post.slug.includes("keep-house-clean-between-cleanings") && (
              <CleaningHabitTrackerDownload />
            )}

            {post.slug.includes("how-often-should-you-clean-your-house") && (
              <div className="my-12">
                <CleaningFrequencyPlanner />
              </div>
            )}

            {post.slug.includes("house-cleaning-cost") && (
              <div className="mt-12">
                <PricingCalculator />
                <BlogLeadCapture />
              </div>
            )}

            <BlogFAQ lang="es" />

            <div className="mt-16 pt-12 border-t">
              <h3 className="text-2xl font-bold mb-8">Explora Nuestros Servicios</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Link to="/es/limpieza-estandar" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                  <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Limpieza Estándar</h4>
                  <p className="text-sm text-muted-foreground">Perfecto para el mantenimiento regular y mantener un hogar impecable.</p>
                </Link>
                <Link to="/es/limpieza-profunda" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                  <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Limpieza Profunda</h4>
                  <p className="text-sm text-muted-foreground">Limpieza detallada de arriba a abajo para hogares que necesitan cuidado adicional.</p>
                </Link>
                <Link to="/es/limpieza-mudanza" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                  <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Mudanzas (Entrada/Salida)</h4>
                  <p className="text-sm text-muted-foreground">Limpieza integral para casas vacías para asegurar la devolución del depósito.</p>
                </Link>
              </div>
            </div>

            {/* Sticky side share bar (desktop xl+) */}
            <ShareBarEs title={post.title} variant="sticky-side" />
            {/* Horizontal share bar below content */}
            <ShareBarEs title={post.title} />

            <div className="mt-10 pt-12 border-t">
              <h3 className="text-2xl font-bold mb-8">También te podría interesar</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {otherPosts.map((other) => (
                  <Link key={other.slug} to={`/es/blog/${other.slug}`} className="group">
                    <div className="aspect-video rounded-2xl overflow-hidden mb-4">
                      <img src={other.image} alt={other.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <h4 className="text-xl font-bold group-hover:text-primary transition-colors leading-tight">
                      {other.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                      <span>{other.date}</span>
                      <span>•</span>
                      <span>{other.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Sticky Why Recurring Banner */}
        <div className="sticky bottom-6 mx-auto max-w-3xl z-40 px-4 pb-6 animate-in slide-in-from-bottom-10 fade-in duration-700">
          <Link to="/why-recurring" className="flex items-center justify-between bg-primary text-primary-foreground p-4 md:p-6 rounded-2xl shadow-2xl hover:bg-primary-hover transition-all group border-2 border-white/20">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full hidden sm:block">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg leading-tight">¿Quieres un hogar impecable todo el año?</h4>
                <p className="text-primary-foreground/80 text-sm">Descubre por qué las limpiezas recurrentes te ahorran hasta un 35%.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-bold whitespace-nowrap">
              Saber más <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SpanishBlogPostPage;
